import {
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  signInWithCustomToken as firebaseSignInWithCustomToken,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  UserCredential,
  User,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  EmailAuthProvider,
} from 'firebase/auth';
import { auth } from '../utils/firebaseSetUp';
import { isSelfHosted } from '../config/infra';
import { selfHostedLogin, clearSelfHostedSession } from './selfHostedAuth';
import { getCurrentAuthUser } from '../utils/currentAuthUser';

export interface AuthError {
  code: string;
  message: string;
}

export interface SignInResult {
  user: User;
}

export const signInWithEmailAndPassword = async (
  email: string,
  password: string
): Promise<SignInResult> => {
  if (isSelfHosted) {
    const { user } = await selfHostedLogin(email, password);
    return {
      user: {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
      } as User,
    };
  }
  try {
    const userCredential = await firebaseSignInWithEmailAndPassword(
      auth,
      email,
      password
    );
    return { user: userCredential.user };
  } catch (error) {
    throw error as AuthError;
  }
};

export const signIn = async (
  email: string,
  password: string
): Promise<SignInResult> => {
  return signInWithEmailAndPassword(email, password);
};

export const sendPasswordReset = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    throw error as AuthError;
  }
};

export const signUp = async (
  email: string,
  password: string
): Promise<SignInResult> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    return { user: userCredential.user };
  } catch (error) {
    throw error as AuthError;
  }
};

export const signInWithCustomToken = async (
  customToken: string
): Promise<SignInResult> => {
  try {
    const userCredential = await firebaseSignInWithCustomToken(
      auth,
      customToken
    );
    return { user: userCredential.user };
  } catch (error) {
    throw error as AuthError;
  }
};

const googleProvider = new GoogleAuthProvider();

export const signInWithGoogle = async (): Promise<UserCredential> => {
  try {
    const userCredential = await signInWithPopup(auth, googleProvider);
    return userCredential;
  } catch (error) {
    throw error as AuthError;
  }
};

export const signOut = async (): Promise<void> => {
  if (isSelfHosted) {
    clearSelfHostedSession();
    return;
  }
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    throw error as AuthError;
  }
};

/**
 * Re-authenticate the current user with email and password.
 * Use before sensitive actions (e.g. updating fiscal data).
 */
export const reauthenticateWithEmailAndPassword = async (
  email: string,
  password: string
): Promise<void> => {
  if (isSelfHosted) {
    await selfHostedLogin(email, password);
    return;
  }
  const user = auth.currentUser;
  if (!user) throw new Error('No hay sesión activa');
  const credential = EmailAuthProvider.credential(email, password);
  await reauthenticateWithCredential(user, credential);
};

/**
 * Re-authenticate the current user with Google popup.
 * Use for users who signed in with Google.
 */
export const reauthenticateWithGoogle = async (): Promise<void> => {
  const user = auth.currentUser;
  if (!user) throw new Error('No hay sesión activa');
  await reauthenticateWithPopup(user, new GoogleAuthProvider());
};

/** True if the current user signed in with email/password (can reauth with password). */
export const isEmailPasswordUser = (): boolean => {
  if (isSelfHosted) {
    return Boolean(getCurrentAuthUser()?.email);
  }
  const user = auth.currentUser;
  return user?.providerData?.some((p) => p.providerId === 'password') ?? false;
};
